import API_ENDPOINTS from "@/common/api/apiEndpoints";
import httpClient from "@/common/api/httpClient";
import httpMethods from "@/common/api/httpMethods";
import EnvConfig from "@/config/env";

export const startTestGen = (payload: Record<string, unknown>) => {
  const endpoint = `${EnvConfig.driverURL}/${API_ENDPOINTS.AI.generateTests}`;
  return httpClient<{ generationId: string }>(
    httpMethods.POST,
    endpoint,
    payload,
  );
};

export const cancelTestGen = (generationId: string) => {
  const endpoint = `${EnvConfig.driverURL}/${API_ENDPOINTS.AI.cancelTests}`;
  return httpClient<{ message: string; generationId: string }>(
    httpMethods.POST,
    endpoint,
    {
      generationId,
    },
  );
};

/**
 * Durable background-generation jobs (issue #1876 Phase 2). Let the frontend
 * recover an in-flight / recently-completed generation after a hard refresh, in
 * a new tab, or once the in-memory SSE replay window has expired.
 */
export interface GenerationJobDto {
  generationId: string;
  kind?: "test_case" | "requirement" | string;
  mode: "manual" | "automation" | string;
  status: "running" | "completed" | "failed" | "cancelled" | string;
  progress: number;
  message: string | null;
  folderId: number | null;
  folderName: string | null;
  prompt: string | null;
  requirementIds: Array<number | string> | null;
  resultCount: number;
  hasResult: boolean;
  error: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  completedAt: string | null;
}

export const getActiveGenerationJobs = (
  workspaceVersionId?: number | string | null,
  kind?: "test_case" | "requirement",
) => {
  const params = new URLSearchParams();
  if (workspaceVersionId != null)
    params.set("workspaceVersionId", String(workspaceVersionId));
  if (kind) params.set("kind", kind);
  const qs = params.toString() ? `?${params.toString()}` : "";
  const endpoint = `${EnvConfig.driverURL}/${API_ENDPOINTS.AI.generationJobsActive}${qs}`;
  return httpClient<{ jobs: GenerationJobDto[] }>(httpMethods.GET, endpoint);
};

export const getGenerationJob = (generationId: string) => {
  const endpoint = `${EnvConfig.driverURL}/${API_ENDPOINTS.AI.generationJob}/${encodeURIComponent(generationId)}`;
  return httpClient<{ job: GenerationJobDto & { result?: unknown } }>(
    httpMethods.GET,
    endpoint,
  );
};

export const dismissGenerationJob = (generationId: string) => {
  const endpoint = `${EnvConfig.driverURL}/${API_ENDPOINTS.AI.generationJob}/${encodeURIComponent(generationId)}/dismiss`;
  return httpClient<{ dismissed: boolean }>(httpMethods.POST, endpoint, {});
};

export interface RequirementGenPayload {
  prompt: string;
  generationId?: string;
  fileBase64?: string;
  fileName?: string;
  syncId?: number | string;
  requirementTypes?: string[];
  workspaceVersionId?: number | string;
}

export const startRequirementGen = (payload: RequirementGenPayload) => {
  const endpoint = `${EnvConfig.driverURL}/${API_ENDPOINTS.AI.generateRequirements}`;
  return httpClient<{ generationId: string }>(
    httpMethods.POST,
    endpoint,
    payload,
  );
};

export interface AutomationStepGenPayload {
  prompt: string;
  generationId: string;
  testCaseDetail?: Record<string, any>;
  workspaceVersionId?: number | string;
  projectId?: number | string;
  websiteUrl?: string;
}

export const startAutomationStepGen = (payload: AutomationStepGenPayload) => {
  const endpoint = `${EnvConfig.driverURL}/${API_ENDPOINTS.AI.generateAutomationSteps}`;
  return httpClient<{ generationId: string }>(
    httpMethods.POST,
    endpoint,
    payload,
  );
};

export interface ManualStepGenPayload {
  prompt: string;
  generationId: string;
  testCaseDetail?: Record<string, any>;
  workspaceVersionId?: number | string;
  websiteUrl?: string;
  /** Manual steps already on the case, so the AI appends without duplicating. */
  existingSteps?: Array<{ step?: string; expected?: string }>;
}

export const startManualStepGen = (payload: ManualStepGenPayload) => {
  const endpoint = `${EnvConfig.driverURL}/${API_ENDPOINTS.AI.generateManualSteps}`;
  return httpClient<{ generationId: string }>(
    httpMethods.POST,
    endpoint,
    payload,
  );
};

export interface PromptSuggestionsPayload {
  workspaceVersionId: number | string;
  websiteUrl?: string;
  existingSteps?: any[];
  testCaseName?: string;
  description?: string;
}

export const fetchPromptSuggestions = (payload: PromptSuggestionsPayload) => {
  const endpoint = `${EnvConfig.driverURL}/${API_ENDPOINTS.AI.promptSuggestions}`;
  return httpClient<{ suggestions: string[] }>(
    httpMethods.POST,
    endpoint,
    payload,
  );
};

export interface AiTestDataDataset {
  name: string;
  description: string;
  expectedToFail: boolean;
  values: Record<string, string>;
}

export interface AiTestDataResult {
  testDataName: string;
  fields: string[];
  datasets: AiTestDataDataset[];
}

export const generateAiTestData = (prompt: string) => {
  const endpoint = `${EnvConfig.driverURL}/${API_ENDPOINTS.AI.generateTestData}`;
  return httpClient<AiTestDataResult>(httpMethods.POST, endpoint, { prompt });
};

export const fetchJiraProjects = () => {
  const endpoint = `${EnvConfig.driverURL}/${API_ENDPOINTS.INTEGRATION.fetchJiraProjects}`;
  return httpClient(httpMethods.GET, endpoint);
};

export const fetchJiraIssues = (projectKey: string) => {
  const endpoint = `${EnvConfig.driverURL}/${API_ENDPOINTS.INTEGRATION.fetchJiraIssues}?projectKey=${projectKey}`;
  return httpClient(httpMethods.GET, endpoint);
};

export interface DefectAiContext {
  testCaseName?: string;
  stepAction?: string;
  result: string;
  message?: string;
  errorCode?: number;
  webDriverException?: string;
  screenshotUrl?: string;
  traceFileUrl?: string;
  durationMs?: number;
  metadata?: Record<string, any>;
}

export interface DefectAiSuggestion {
  title: string;
  description: string;
  priority: string;
  severity: string;
}

export const suggestDefect = async (
  payload: DefectAiContext,
): Promise<DefectAiSuggestion> => {
  const endpoint = `${EnvConfig.driverURL}/${API_ENDPOINTS.AI.suggestDefect}`;
  const response = await httpClient<DefectAiSuggestion>(
    httpMethods.POST,
    endpoint,
    payload,
  );
  return response.data;
};

export interface CrawlSettings {
  crawlDepth: number;
  maxClicksPerPage: number;
}

export interface CrawlStartPayload {
  sourceType: "github" | "zip" | "url" | "url+github" | "url+zip";
  githubUrl?: string;
  appUrl?: string;
  zipBase64?: string;
  zipFileName?: string;
  generationId: string;
  projectId?: number;
  settings: CrawlSettings;
}

export interface CrawlAuthPayload {
  strategy: "form" | "cookie" | "token";
  username?: string;
  password?: string;
  loginUrl?: string;
  cookieJson?: string;
  apiToken?: string;
}

export interface DiscoveredPage {
  url: string;
  name: string;
  category: "auth" | "settings" | "dashboard" | "admin" | "public" | "other";
  isAuthProtected: boolean;
  routePath: string;
}

export interface CrawlStartResponse {
  sessionId: string;
  generationId: string;
}

export interface CrawlAuthResponse {
  success: boolean;
}

export interface CrawlConfirmPayload {
  selectedRoutes: string[];
  generationId: string;
  projectId?: number;
}

export interface CrawlConfirmResponse {
  success: boolean;
  generationId: string;
}

export const startCrawl = (payload: CrawlStartPayload) => {
  const endpoint = `${EnvConfig.driverURL}/${API_ENDPOINTS.AI.startCrawl}`;
  return httpClient<CrawlStartResponse>(httpMethods.POST, endpoint, payload);
};

export const submitCrawlAuth = (
  sessionId: string,
  authData: CrawlAuthPayload,
) => {
  const endpoint = `${EnvConfig.driverURL}/${API_ENDPOINTS.AI.crawlAuth}/${sessionId}`;
  return httpClient<CrawlAuthResponse>(httpMethods.POST, endpoint, authData);
};

export const confirmCrawlPages = (
  sessionId: string,
  payload: CrawlConfirmPayload,
) => {
  const endpoint = `${EnvConfig.driverURL}/${API_ENDPOINTS.AI.crawlConfirm}/${sessionId}`;
  return httpClient<CrawlConfirmResponse>(httpMethods.POST, endpoint, payload);
};

export interface GeneratedTitle {
  id: string;
  routePath: string;
  name: string;
  description: string;
  priority: string;
  /** Additive — present on cross-page (journey) titles */
  kind?: "page" | "journey";
  /** Additive — links a journey-kind title back to its DiscoveredJourney */
  journeyId?: string;
}

export interface JourneyStep {
  routePath: string;
  action?: {
    elementName: string;
    elementRole: string;
    outcome: "navigation" | "modal" | "content";
  };
}

export interface DiscoveredJourney {
  id: string;
  name: string;
  description?: string;
  steps: JourneyStep[];
  /**
   * Additive — backend may include the future-saved step-group name for a
   * journey-typed scenario. Used by TitleReview to preview the reusable
   * Step Group label before persistence (ADR-009 / ADR-015).
   */
  suggestedStepGroupName?: string;
}

/**
 * Emitted by SSE after the backend persists a journey's steps as a reusable
 * Step Group (`update / step_group_persisted`). Additive / backward-compatible:
 * older backends never emit this event and the UI must tolerate its absence.
 */
export interface PersistedStepGroup {
  id: string | number;
  name: string;
  /** Optional — links back to the originating DiscoveredJourney */
  journeyId?: string;
  /** Optional — used to associate a group with the title that produced it */
  testCaseId?: string;
  /** Optional — number of steps in the group, for the toast / sidebar entry */
  stepCount?: number;
  /** Optional — origin route (first journey step) for context */
  routePath?: string;
  /** Optional — wall-clock timestamp (ISO) for ordering in the sidebar */
  createdAt?: string;
}

export interface GenerateTitlesPayload {
  selectedRoutes: string[];
  generationId: string;
}

export interface GenerateStepsPayload {
  approvedIds: string[];
  generationId: string;
  folderId?: number;
  /** Workspace version scope so the durable job rehydrates for the right workspace (#1876 #17). */
  workspaceVersionId?: number | string;
  /** Folder name for the background indicator label. */
  folderName?: string;
}

export const generateCrawlTitles = (
  sessionId: string,
  payload: GenerateTitlesPayload,
) => {
  const endpoint = `${EnvConfig.driverURL}/${API_ENDPOINTS.AI.crawlTitles}/${sessionId}`;
  return httpClient<{ generationId: string; sessionId: string }>(
    httpMethods.POST,
    endpoint,
    payload,
  );
};

export const generateCrawlSteps = (
  sessionId: string,
  payload: GenerateStepsPayload,
) => {
  const endpoint = `${EnvConfig.driverURL}/${API_ENDPOINTS.AI.crawlSteps}/${sessionId}`;
  return httpClient<{ generationId: string; sessionId: string }>(
    httpMethods.POST,
    endpoint,
    payload,
  );
};

export const continueCrawl = (
  sessionId: string,
  generationId: string,
  extraPages = 50,
) => {
  const endpoint = `${EnvConfig.driverURL}/${API_ENDPOINTS.AI.crawlContinue}/${sessionId}`;
  return httpClient<{ generationId: string; sessionId: string }>(
    httpMethods.POST,
    endpoint,
    {
      extraPages,
      generationId,
    },
  );
};
